// =============================================================================
// GENERATION RECIPE (feature « Iterate ») — transforms purs, testables sans
// React ni Firebase :
//   - buildGenerationRecipe : capture du snapshot à l'enqueue (blocs éphémères
//     exclus, flags transitoires strippés, prompts utilisées embarquées) ;
//   - instantiateRecipe : transform de restore (tous les blocs verrouillés,
//     blocs character-lora sans personnage disponible restaurés sans leur
//     characterId — ils continuent d'émettre leur trigger word comme texte).
// =============================================================================

import type { Block, ProjectPrompt, Wire } from '../types';
import { GENERATION_RECIPE_VERSION, type GenerationRecipe } from '../types/recipe';
import { stripTransientBlockFlags, stripTransientWireFlags } from './exportWorkflow';

// Blocs éphémères de panneau exclus de la recette — même filtrage que
// applyRemoteData (useCanvasPersistence) et exportWorkflow : ces blocs sont
// des panneaux, pas du contenu.
const EPHEMERAL_BLOCK_TYPES = new Set<Block['type']>([
  'generate',
  'prompt-length',
  'prompt-puzzle',
]);

/**
 * Supprime récursivement les champs `undefined` (Firestore les refuse et le
 * payload de la callable ne les sérialise pas) — même helper que
 * useCanvasPersistence, dupliqué ici pour rester pur et testable.
 * Exporté pour l'élagage de recette des favoris (favoriteRecipe.ts).
 */
export const deepStripUndefined = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(deepStripUndefined) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const cleaned: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      if (val !== undefined) {
        cleaned[key] = deepStripUndefined(val);
      }
    }
    return cleaned as unknown as T;
  }
  return value;
};

export interface BuildRecipeParams {
  /** Blocs du canvas APRÈS applyPromptSelections (selectedPromptId résolus). */
  blocks: Block[];
  wires: Wire[];
  promptsByCategory: Record<string, ProjectPrompt[]>;
  /** Pools des prompt sets référencés, identiques à ceux de composePrompt. */
  promptSetsById?: Record<string, ProjectPrompt[]>;
  model: string;
  /** Image : dimensions demandées. Vidéo ltx25 : réglages du panneau. */
  width?: number;
  height?: number;
  mediaType?: 'video';
  duration?: number;
  aspectRatio?: string;
  audio?: boolean;
  hd?: boolean;
}

/**
 * Construit la recette d'une génération à partir de l'état du canvas à
 * l'enqueue. La seed n'y figure PAS (tirée côté serveur) — elle est ajoutée
 * à la complétion (run de session) / à la persistance (promptHistory).
 * Fonction pure — testable sans React ni Firebase.
 */
export const buildGenerationRecipe = (params: BuildRecipeParams): GenerationRecipe => {
  // 1. Filtre les blocs éphémères et nettoie les flags transitoires.
  const blocks = params.blocks
    .filter((b) => !EPHEMERAL_BLOCK_TYPES.has(b.type))
    .map(stripTransientBlockFlags);

  // 2. Droppe les wires orphelins (extrémité sur un bloc exclu).
  const blockIds = new Set(blocks.map((b) => b.id));
  const wires = params.wires
    .filter((w) => blockIds.has(w.from) && blockIds.has(w.to))
    .map(stripTransientWireFlags);

  // 3. Prompts réellement utilisées par la composition (selectedPromptId
  //    résolus sur les blocs) — embarquées, dédupliquées. Le pool effectif
  //    du prompt set prime sur le pool projet, comme dans composePrompt.
  //    addedAt exclu : Timestamp Firestore non sérialisable dans le payload.
  const prompts: ProjectPrompt[] = [];
  const seen = new Set<string>();
  for (const block of blocks) {
    if (!block.selectedPromptId || seen.has(block.selectedPromptId)) continue;
    const setCandidates = block.promptSetId
      ? params.promptSetsById?.[block.promptSetId]
      : undefined;
    const candidates = setCandidates ?? params.promptsByCategory[block.type] ?? [];
    const prompt = candidates.find((p) => p.promptId === block.selectedPromptId);
    if (!prompt) continue;
    seen.add(prompt.promptId);
    const { addedAt, ...serializable } = prompt;
    prompts.push(serializable);
  }

  return deepStripUndefined({
    version: GENERATION_RECIPE_VERSION,
    blocks,
    wires,
    prompts,
    model: params.model,
    width: params.width,
    height: params.height,
    mediaType: params.mediaType,
    duration: params.duration,
    aspectRatio: params.aspectRatio,
    audio: params.audio,
    hd: params.hd,
  });
};

export interface InstantiatedRecipe {
  blocks: Block[];
  wires: Wire[];
  prompts: ProjectPrompt[];
  /** Noms (customLabel) des blocs character-lora restaurés SANS leur
      characterId — le personnage n'existe plus ou n'est plus 'ready'
      (toaster côté UI). */
  droppedCharacterNames: string[];
}

/** Garde runtime minimale pour les données lues hors du typage Firestore. */
export const isRestorableGenerationRecipe = (value: unknown): value is GenerationRecipe => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const recipe = value as Partial<GenerationRecipe>;
  return (
    recipe.version === GENERATION_RECIPE_VERSION &&
    Array.isArray(recipe.blocks) &&
    Array.isArray(recipe.wires) &&
    Array.isArray(recipe.prompts)
  );
};

/**
 * Transform de restore : TOUS les blocs verrouillés (le verrouillage n'existe
 * qu'au restore, jamais dans la recette stockée), flags transitoires strippés,
 * prompt sets détachés (leurs prompts sélectionnées sont embarquées), wires
 * orphelins droppés, prompts dédupliquées.
 * `readyCharacterIds` : personnages encore utilisables (status 'ready') — un
 * bloc character-lora dont le personnage a disparu est restauré sans
 * characterId (trigger word / customLabel / cover conservés) pour ne jamais
 * envoyer au serveur un LoRA introuvable.
 * Fonction pure — testable sans React ni Firebase.
 */
export const instantiateRecipe = (
  recipe: GenerationRecipe,
  readyCharacterIds: ReadonlySet<string>
): InstantiatedRecipe => {
  const droppedCharacterNames: string[] = [];

  const blocks = recipe.blocks.map((raw) => {
    const block: Block = { ...stripTransientBlockFlags(raw), locked: true };
    delete block.promptSetId;
    delete block.promptSetName;
    if (
      block.type === 'character-lora' &&
      block.characterId &&
      !readyCharacterIds.has(block.characterId)
    ) {
      droppedCharacterNames.push(block.customLabel ?? 'Character');
      delete block.characterId;
    }
    return block;
  });

  const blockIds = new Set(blocks.map((b) => b.id));
  const wires = recipe.wires
    .filter((w) => blockIds.has(w.from) && blockIds.has(w.to))
    .map(stripTransientWireFlags);

  const seen = new Set<string>();
  const prompts = recipe.prompts.filter((p) => {
    if (seen.has(p.promptId)) return false;
    seen.add(p.promptId);
    return true;
  });

  return { blocks, wires, prompts, droppedCharacterNames };
};
